import { useMemo } from 'react';
import { Pencil, Eye, Download } from 'lucide-react';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { CsvViewer } from 'src/shared/components/thread-store/csv/CsvViewer';
import { useExpandedView } from 'src/shared/components/thread-store/context/ExpandedViewContext';
import { ThreadStore } from 'src/features/flows/types/threadStore.types';
import { Separator } from 'src/shared/components/ui/separator';

interface ApproveCsvPreviewProps {
  label: string;
  value: string;
  containsHeader: boolean;
  valueToApproveId: string;
  threadStore?: ThreadStore;
  /** Whether to show the download button. Default: true */
  showDownload?: boolean;
  description?: string;
}

/**
 * CSV preview component for approval values.
 * Platform-agnostic - uses useExpandedView for opening the editor.
 */
export function ApproveCsvPreview({
  label,
  value,
  containsHeader,
  valueToApproveId,
  threadStore,
  showDownload = true,
  description,
}: ApproveCsvPreviewProps) {
  const { openExpandedView } = useExpandedView();

  const { headers, dataRows } = useMemo(() => {
    if (!value || typeof value !== 'string') {
      return { headers: [], dataRows: [] };
    }

    try {
      const parsed = Papa.parse<string[]>(value, {
        header: false,
        skipEmptyLines: true,
      });

      const allRows = parsed.data;

      if (!allRows.length) {
        return { headers: [], dataRows: [] };
      }

      if (containsHeader) {
        return {
          headers: allRows[0],
          dataRows: allRows.slice(1),
        };
      }

      return {
        headers: [],
        dataRows: allRows,
      };
    } catch (error) {
      console.error('Error parsing CSV:', error);
      return { headers: [], dataRows: [] };
    }
  }, [value, containsHeader]);

  const approvalPending = useMemo(
    () => threadStore?.type === 'approval' && ['pending', 'failed'].includes(threadStore?.data.status),
    [threadStore]
  );

  const handleEditOrView = () => {
    if (!threadStore) return;

    openExpandedView({
      type: 'csv-editor',
      data: {
        threadStoreId: threadStore.id,
        integrationId: threadStore.integrationId,
        threadId: threadStore.threadId,
        valueToApproveId,
        editable: approvalPending,
        title: label,
        file: value,
        containsHeader,
        filename: `${label}.csv`,
      },
    });
  };

  const handleDownload = () => {
    const blob = new Blob([value], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${label}.csv`);
  };

  return (
    <div className="flex flex-col border rounded-sm bg-white py-1 w-full">
      <div className="flex flex-row justify-between items-center mb-1 px-2 pl-3">
        <div className="flex flex-col justify-start flex-1 min-w-0">
          <span className="text-sm font-medium">{label}</span>
          {description && <span className="text-xs text-typography-grey truncate">{description}</span>}
          {dataRows.length > 0 && <span className="text-xs text-gray-500">{dataRows.length} rows</span>}
        </div>
        <div className="flex flex-row gap-1 items-center">
          {showDownload && (
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-neutral-500 hover:text-neutral-800 transition-colors"
              aria-label="Download CSV"
            >
              <Download size={12} />
            </button>
          )}
          {threadStore && (
            <button
              type="button"
              onClick={handleEditOrView}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-sm hover:bg-gray-50 transition-colors"
            >
              {approvalPending ? (
                <>
                  Edit
                  <Pencil size={12} />
                </>
              ) : (
                <>
                  View
                  <Eye size={12} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <Separator />
      <CsvViewer maxHeight="400px" headers={headers} rows={dataRows} />
    </div>
  );
}

export default ApproveCsvPreview;
